import React, { useCallback, useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { Crosshair, History, Target, Trophy, UserRound, Wallet } from 'lucide-react';
import Spinner from '../../components/UI/Spinner';
import EmptyState from '../../components/UI/EmptyState';
import {
  AddressLink,
  PredictionTicket,
  StatTile,
  TierBadge,
  btc,
  usd,
} from './components';
import { USDT_DECIMALS, WBTC_DECIMALS } from './config';
import { useT } from '../../i18n';

/** How many rounds back from the player's last activity the history walks. */
const HISTORY_DEPTH = 6;

const toNum = (value, decimals) =>
  parseFloat(ethers.utils.formatUnits(value.toString(), decimals));

const readStats = (raw) => ({
  totalBets: Number(raw.totalBets ? raw.totalBets.toString() : raw[0].toString()),
  totalWins: Number(raw.totalWins ? raw.totalWins.toString() : raw[1].toString()),
  exactPredictions: Number(
    raw.exactPredictions ? raw.exactPredictions.toString() : raw[2].toString()
  ),
  prizesWon: toNum(raw.prizesWon ? raw.prizesWon : raw[3], USDT_DECIMALS),
  wbtcPrizesWon: toNum(raw.wbtcPrizesWon ? raw.wbtcPrizesWon : raw[4], WBTC_DECIMALS),
  lastActiveRound: Number(
    raw.lastActiveRound ? raw.lastActiveRound.toString() : raw[5].toString()
  ),
});

const Profile = ({ game }) => {
  const t = useT();
  const {
    contract,
    address,
    isConnected,
    month,
    usdtBalance,
    wbtcBalance,
    myLiveBets,
    myNextBets,
    liveRoundId,
    nextRoundId,
    readRoundBets,
  } = game;

  const [allTime, setAllTime] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [score, setScore] = useState(null);
  const [rank, setRank] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    if (!address) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [statsAll, statsMonth, playerScore, board] = await Promise.all([
        contract.getPlayerStatsAllTime(address).catch(() => null),
        contract.getPlayerStatsMonthly(address, month).catch(() => null),
        contract.calculatePlayerScore(address).catch(() => null),
        contract.getLeaderboard(0, 100).catch(() => []),
      ]);

      const parsed = statsAll ? readStats(statsAll) : null;
      setAllTime(parsed);
      setMonthly(statsMonth ? readStats(statsMonth) : null);
      setScore(playerScore ? Number(playerScore.toString()) : null);

      const found = (board || []).findIndex(
        (entry) => (entry.player || entry[0]).toLowerCase() === address.toLowerCase()
      );
      setRank(found >= 0 ? found + 1 : null);

      // History is anchored on the last round the player actually touched, so a
      // dormant account does not trigger a pointless scan of recent rounds.
      const anchor = parsed && parsed.lastActiveRound ? parsed.lastActiveRound : 0;
      if (anchor) {
        setHistoryLoading(true);
        const rounds = [];
        for (let id = anchor; id > Math.max(0, anchor - HISTORY_DEPTH); id -= 1) {
          rounds.push(id);
        }

        const entries = await Promise.all(
          rounds.map(async (id) => {
            const [bets, finalPrice, winners] = await Promise.all([
              readRoundBets(id),
              contract.finalPrices(id).catch(() => null),
              contract.getRoundWinners(id).catch(() => []),
            ]);

            const mine = bets
              .filter((bet) => bet.player.toLowerCase() === address.toLowerCase())
              .map((bet) => bet.price);
            if (!mine.length) return null;

            const myWins = (winners || [])
              .filter((winner) => winner.player.toLowerCase() === address.toLowerCase())
              .map((winner) => ({
                price: Number(winner.predictedPrice.toString()),
                tier: Number(winner.tier.toString()),
                usdt: toNum(winner.usdtPrize, USDT_DECIMALS),
                wbtc: toNum(winner.wbtcPrize, WBTC_DECIMALS),
              }));

            return {
              roundId: id,
              predictions: mine,
              finalPrice: finalPrice ? Number(finalPrice.toString()) : null,
              wins: myWins,
            };
          })
        );

        setHistory(entries.filter(Boolean));
        setHistoryLoading(false);
      } else {
        setHistory([]);
      }
    } catch (err) {
      console.error('[BullsEye] profile read failed', err);
    } finally {
      setLoading(false);
    }
  }, [contract, address, month, readRoundBets]);

  useEffect(() => {
    load();
  }, [load]);

  if (!isConnected) {
    return (
      <section className="lb-card">
        <EmptyState
          icon={UserRound}
          title={t('bullseye.pf.connect')}
          message={t('bullseye.pf.connectBlurb')}
        />
      </section>
    );
  }

  if (loading) {
    return (
      <div className="py-24">
        <Spinner size="lg" label={t('bullseye.pf.loading')} />
        <p className="mt-6 text-center text-sm text-white/50">Fetching your betting history…</p>
      </div>
    );
  }

  const winRate =
    allTime && allTime.totalBets ? (allTime.totalWins / allTime.totalBets) * 100 : 0;

  return (
    <div className="space-y-6">
      <section className="lb-section p-6 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="lb-chip lb-chip-cyan">
              <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
              {t('bullseye.tabProfile')}
            </span>
            <h2 className="lb-heading mt-4 text-4xl sm:text-5xl">{t('bullseye.pf.title')}</h2>
            <p className="mt-3 text-sm text-white/70">
              <AddressLink value={address} />
            </p>
          </div>

          <div className="grid w-full gap-3 sm:w-auto sm:grid-cols-3">
            <StatTile
              label={t('bullseye.pf.rank')}
              value={rank ? `#${rank}` : 'Unranked'}
              tone={rank ? 'gold' : 'default'}
            />
            <StatTile label={t('bullseye.pf.score')} value={score !== null ? usd(score) : '-'} tone="gradient" />
            <StatTile label={t('bullseye.pf.winRate')} value={`${winRate.toFixed(1)}%`} />
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[1.55fr,1fr]">
        <div className="space-y-6">
          {/* All-time */}
          <section className="lb-card p-5 sm:p-6">
            <h3 className="lb-heading mb-5 flex items-center gap-2 text-xl">
              <Target className="h-5 w-5 text-brand-cyan" aria-hidden="true" />
              {t('bullseye.pf.allTimeRecord')}
            </h3>
            {allTime ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <StatTile label={t('bullseye.pf.predictionsPlaced')} value={usd(allTime.totalBets)} />
                <StatTile label={t('bullseye.pf.winningRounds')} value={usd(allTime.totalWins)} />
                <StatTile label={t('bullseye.pf.exactHits')} value={usd(allTime.exactPredictions)} tone="gold" />
                <StatTile
                  label={t('bullseye.pf.usdtWon')}
                  value={usd(allTime.prizesWon, 2)}
                  tone="gradient"
                />
                <StatTile label={t('bullseye.pf.wbtcWon')} value={btc(allTime.wbtcPrizesWon)} tone="gold" />
                <StatTile
                  label={t('bullseye.pf.lastActiveRound')}
                  value={allTime.lastActiveRound ? `#${allTime.lastActiveRound}` : '-'}
                />
              </div>
            ) : (
              <p className="text-sm text-white/50">{t('bullseye.pf.noRecord')}</p>
            )}
          </section>

          {/* This month */}
          {monthly ? (
            <section className="lb-card p-5 sm:p-6">
              <h3 className="lb-heading mb-5 flex items-center gap-2 text-xl">
                <Trophy className="h-5 w-5 text-brand-gold" aria-hidden="true" />
                {t('bullseye.pf.thisMonth')}
              </h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatTile label={t('bullseye.pf.predictions')} value={usd(monthly.totalBets)} />
                <StatTile label={t('bullseye.pf.wins')} value={usd(monthly.totalWins)} />
                <StatTile label={t('bullseye.pf.exactHits')} value={usd(monthly.exactPredictions)} />
                <StatTile label={t('bullseye.pf.usdtWon')} value={usd(monthly.prizesWon, 2)} />
              </div>
            </section>
          ) : null}

          {/* History */}
          <section className="lb-card p-5 sm:p-6">
            <h3 className="lb-heading mb-5 flex items-center gap-2 text-xl">
              <History className="h-5 w-5 text-brand-cyan" aria-hidden="true" />
              {t('bullseye.pf.recentRounds')}
            </h3>

            {historyLoading ? (
              <div className="py-10">
                <Spinner label={t('bullseye.pf.loadingHistory')} />
              </div>
            ) : history.length ? (
              <div className="space-y-4">
                {history.map((round) => (
                  <article
                    key={round.roundId}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
                  >
                    <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <p className="notranslate lb-num font-display text-lg text-white">
                        Round #{round.roundId}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="text-white/50">
                          Final price{' '}
                          <span className="notranslate lb-num font-bold text-white">
                            {round.finalPrice ? `$${usd(round.finalPrice)}` : 'pending'}
                          </span>
                        </span>
                        {round.wins.length ? (
                          <span className="lb-chip lb-chip-gold">
                            <Trophy className="h-3.5 w-3.5" aria-hidden="true" />
                            {round.wins.length} win{round.wins.length === 1 ? '' : 's'}
                          </span>
                        ) : null}
                      </div>
                    </header>

                    <div className="flex flex-wrap gap-2">
                      {round.predictions.map((price, index) => {
                        const win = round.wins.find((entry) => entry.price === price);
                        return (
                          <span
                            key={`${round.roundId}-${price}-${index}`}
                            className={`notranslate lb-num inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
                              win
                                ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-200'
                                : 'border-white/10 bg-black/40 text-white/60'
                            }`}
                          >
                            ${usd(price)}
                            {win ? <TierBadge tier={win.tier} /> : null}
                          </span>
                        );
                      })}
                    </div>

                    {round.wins.length ? (
                      <p className="notranslate lb-num mt-3 text-sm font-bold text-emerald-300">
                        +{usd(round.wins.reduce((sum, win) => sum + win.usdt, 0), 2)} USDT
                        {round.wins.some((win) => win.wbtc > 0) ? (
                          <span className="ml-2 text-amber-300">
                            + {btc(round.wins.reduce((sum, win) => sum + win.wbtc, 0))} WBTC
                          </span>
                        ) : null}
                      </p>
                    ) : null}
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={History}
                title={t('bullseye.pf.noHistory')}
                message={t('bullseye.pf.noHistoryBlurb')}
              />
            )}
          </section>
        </div>

        <div className="space-y-6">
          <section className="lb-card p-5 sm:p-6">
            <h3 className="lb-heading mb-5 flex items-center gap-2 text-xl">
              <Wallet className="h-5 w-5 text-brand-cyan" aria-hidden="true" />
              {t('bullseye.pf.balances')}
            </h3>
            <div className="space-y-3">
              <StatTile
                label="USDT"
                value={usd(usdtBalance, 2)}
                sub="Available for predictions"
                tone="gradient"
              />
              <StatTile
                label="WBTC"
                value={btc(wbtcBalance)}
                sub="Jackpot winnings"
                tone="gold"
              />
            </div>
          </section>

          <section className="lb-card p-5 sm:p-6">
            <h3 className="lb-heading mb-5 flex items-center gap-2 text-xl">
              <Crosshair className="h-5 w-5 text-brand-cyan" aria-hidden="true" />
              {t('bullseye.pf.inPlay')}
            </h3>
            {myLiveBets.length || myNextBets.length ? (
              <div className="space-y-5">
                {myLiveBets.length ? (
                  <div>
                    <p className="lb-label mb-3">Round #{liveRoundId}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {myLiveBets.map((price, index) => (
                        <PredictionTicket
                          key={`p-live-${price}-${index}`}
                          price={price}
                          roundId={liveRoundId}
                          index={index}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {nextRoundId !== liveRoundId && myNextBets.length ? (
                  <div>
                    <p className="lb-label mb-3">Round #{nextRoundId}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {myNextBets.map((price, index) => (
                        <PredictionTicket
                          key={`p-next-${price}-${index}`}
                          price={price}
                          roundId={nextRoundId}
                          index={index}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-white/50">{t('bullseye.pf.nothingInPlay')}</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Profile;
